import { observable, action, computed, makeObservable } from 'mobx'
import { Model } from 'infra/model'
import { Id } from 'types/definitions'
import {
  DraftPayoutResource,
  ApprovedTransactionResource,
} from './dtos/drafting'
import once from 'lodash/once'
import { collection } from 'infra/collection'
import { PrimeUser } from 'iam/UserStore'
import User from 'iam/models/User'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import Workspace from 'iam/models/Workspace'
import { ApprovedTransaction } from './ApprovedTransaction'
import PendingTransaction from './PendingTransaction'
import { toPrecision } from '../../utils/number-util'

/**
 * A Payout that is being drafted.
 */
export class DraftPayout extends Model<DraftPayoutResource, DraftPayout> {
  id = Id.none

  @observable etag = ''
  @observable totalAmount = 0

  /**
   * The approved transactions.
   */
  approvedTransactions = collection<
    ApprovedTransactionResource,
    ApprovedTransaction
  >({
    create: () =>
      new ApprovedTransaction(this, this.primeUser, this.primeWorkspace),
  })

  /**
   * The default instance.
   */
  static get none() {
    return lazyNone()
  }

  /**
   * Constructor.
   */
  constructor(
    private primeUser: PrimeUser,
    private primeWorkspace: PrimeWorkspace,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * The approved transactions as a set of IDs.
   */
  @computed
  get approvedTransactionIds() {
    return new Set(this.approvedTransactions.items.map((t) => t.id))
  }

  /**
   * Parses attributes from the resource model.
   *
   * @param attrs
   */
  parse(attrs: Partial<DraftPayoutResource>): Partial<DraftPayout> {
    this.approvedTransactions.reset(attrs.approved_transactions)
    return {
      id: attrs.id,
      etag: attrs.etag,
      totalAmount: attrs.total_amount,
    }
  }

  /**
   * Approves the transactions.
   * Called after having approved the transactions on the server.
   */
  @action
  approveTransactions(
    transactions: PendingTransaction[],
    approvedBy: User,
    approvedAt: Date,
    draftEtag: string,
  ) {
    this.etag = draftEtag
    for (const transaction of transactions) {
      // Add the transaction to the approved collection
      const approvedTransaction = this.approvedTransactions.create({
        id: transaction.id,
      })

      approvedTransaction.approvedBy = approvedBy
      approvedTransaction.approvedAt = approvedAt

      // Copy over the attributes from the pending transaction.
      approvedTransaction.createdAt = transaction.createdAt
      approvedTransaction.transactedAt = transaction.transactedAt
      approvedTransaction.shortId = transaction.shortId
      approvedTransaction.amount = transaction.amount
      approvedTransaction.type = transaction.type
      approvedTransaction.recipient = transaction.recipient
      approvedTransaction.workspace = transaction.workspace
      approvedTransaction.source = transaction.source
      approvedTransaction.reason = transaction.reason
      approvedTransaction.jobId = transaction.jobId
      approvedTransaction.flags = transaction.flags.slice()

      this.totalAmount = toPrecision(this.totalAmount + transaction.amount)
    }
  }

  /**
   * Removes the transactions.
   * Called after having removed the transactions from the
   * payout on the server.
   */
  @action
  removeTransactions(transactions: ApprovedTransaction[], draftEtag: string) {
    this.etag = draftEtag
    for (const transaction of transactions) {
      this.totalAmount = toPrecision(this.totalAmount - transaction.amount)
      this.approvedTransactions.remove(transaction)
    }
  }
}

/**
 * Lazy getter for the default instance.
 */
const lazyNone = once(
  () =>
    new DraftPayout(
      () => User.none,
      () => Workspace.none,
    ),
)
